
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const BellvillePage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Bellville page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Bellville Painters",
      "description": "Professional painting contractors in Bellville, Western Cape. Get quotes for interior, exterior, roof and commercial painting services.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/bellville-painters`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bellville",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8903",
        "longitude": "18.6292"
      },
      "serviceArea": {
        "@type": "City",
        "name": "Bellville"
      },
      "services": [
        "Residential Interior Painting",
        "Exterior House Painting",
        "Roof Painting",
        "Commercial Painting",
        "Specialty Finishes",
        "Surface Preparation"
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Bellville Painters - Best Painting Contractors Western Cape | Free Quotes";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting contractors in Bellville, Western Cape. Get free quotes for interior, exterior, roof and commercial painting. Vetted painters, best prices guaranteed.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/6c821d0a0a2eec08188d8952fa367dac.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="block">Bellville</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get quotes from the best painting contractors in Bellville
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Painting Services in Bellville
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Looking for reliable painting contractors in Bellville? We connect you with the area's most trusted and skilled painting professionals who deliver exceptional results for both residential and commercial properties throughout the Bellville region.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our network of Bellville painters specializes in interior painting, exterior painting, roof painting, and commercial painting services. Each contractor is thoroughly vetted to ensure you receive the highest quality workmanship and professional service that meets the unique needs of Bellville homeowners and businesses.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're renovating your family home in Bellville South, updating your office space in the Bellville CBD, or maintaining your commercial property in the greater Bellville area, our painting contractors have the expertise and experience to transform your vision into reality while respecting your budget and timeline.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Interior & Exterior Painting</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Roof Painting & Restoration</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Commercial Painting</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Free Quotes & Consultations</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20beautiful%20residential%20home%20in%20Bellville%20with%20modern%20suburban%20setting%2C%20quality%20painting%20equipment%2C%20clean%20professional%20appearance%2C%20bright%20natural%20lighting&width=600&height=400&seq=bellville-painting-work&orientation=landscape"
                alt="Professional painters working in Bellville"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Bellville Painting Market */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Understanding Bellville's Unique Painting Requirements
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6">
                Bellville's diverse architectural landscape presents unique challenges and opportunities for painting contractors. From the historic buildings in the Bellville CBD to the modern developments in Bellville South and the established neighborhoods throughout the area, each property type requires specialized knowledge and techniques.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The Western Cape's Mediterranean climate means Bellville properties face specific environmental challenges including UV exposure, seasonal rainfall, and coastal humidity. Our network of Bellville painting professionals understands these local conditions and selects appropriate paint systems and application methods to ensure long-lasting, beautiful results.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Whether you're dealing with the older brick and plaster homes common in established Bellville neighborhoods or the contemporary finishes found in newer developments, our contractors bring the right expertise to handle any surface preparation and painting challenge your Bellville property may present.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Bellville%20South%20Africa%20suburban%20neighborhood%20with%20diverse%20architectural%20styles%2C%20modern%20and%20traditional%20homes%2C%20tree-lined%20streets%2C%20Western%20Cape%20setting%2C%20professional%20painting%20work%20visible&width=600&height=400&seq=bellville-neighborhood&orientation=landscape"
                alt="Bellville neighborhood architecture"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Bellville Painting Services
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional painting solutions tailored for Bellville's residential and commercial properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Bellville Residential Interior Painting</h5>
              <p className="text-gray-600">Transform your Bellville home with professional interior painting services. From single rooms to whole house makeovers, our contractors understand the unique style preferences of Bellville homeowners.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Bellville Exterior House Painting</h5>
              <p className="text-gray-600">Protect and beautify your Bellville property's exterior with weather-resistant paints specifically chosen for the Western Cape climate and Bellville's environmental conditions.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Bellville Roof Painting</h5>
              <p className="text-gray-600">Extend your Bellville property's roof lifespan and improve curb appeal with professional roof painting and restoration services designed for local tile and metal roofing systems.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Bellville Commercial Painting</h5>
              <p className="text-gray-600">Professional painting services for Bellville businesses, from small offices in the CBD to large industrial facilities, retail spaces, and commercial developments throughout the area.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-brush-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Bellville Specialty Finishes</h5>
              <p className="text-gray-600">Decorative painting, texture work, and specialty finishes to create unique visual effects that complement Bellville's diverse architectural styles and homeowner preferences.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Bellville Surface Preparation</h5>
              <p className="text-gray-600">Thorough surface preparation including cleaning, sanding, priming, and repair work specifically tailored for Bellville's building materials and environmental conditions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Bellville Painters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Bellville Painting Contractors
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We connect you with Bellville's most trusted and experienced painting professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Bellville Local Expertise</h5>
              <p className="text-gray-600">Our contractors know Bellville's climate, architectural styles, and local building requirements, ensuring the best results for your specific property type and location.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Bellville Quality Guaranteed</h5>
              <p className="text-gray-600">All our Bellville painters are committed to delivering exceptional workmanship that meets the high standards expected by discerning Bellville property owners.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Bellville Timely Service</h5>
              <p className="text-gray-600">Reliable contractors who understand Bellville residents' busy lifestyles and respect your schedule, completing projects on time and within budget.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Bellville Competitive Pricing</h5>
              <p className="text-gray-600">Compare multiple quotes from qualified Bellville painters to get the best value for your investment while maintaining the quality standards your property deserves.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bellville Area Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Bellville Areas We Serve
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Bellville Central</h6>
              <p className="text-gray-600">Professional painting services for the historic Bellville CBD, including commercial buildings, offices, and mixed-use developments.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Bellville South</h6>
              <p className="text-gray-600">Residential painting specialists for the established neighborhoods and family homes throughout the Bellville South area.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Greater Bellville</h6>
              <p className="text-gray-600">Comprehensive painting services for all surrounding Bellville areas, including industrial zones and new residential developments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Bellville Customers Say
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Bellville customers who found their perfect painting contractor through our service
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Bellville Painting Quotes
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your Bellville property? Connect with the best painting contractors in your area and receive multiple competitive quotes
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default BellvillePage;
